import React, { FunctionComponent } from 'react';
import { pipe, pickBy, isEmpty, filter, toPairs } from 'ramda';
import { FormattedMessage } from 'react-intl';
import { ReferenceType } from '../../../../common/enums/ReferenceType';
import { IReferenceScore } from '../../../../common/models/reference/IReferenceScore';
import CustomPopover from '../../../../components/CustomPopover';
import { ProfileRefCounters } from '../../../../common/models/reference/ProfileRefCounters';
import Icon from '../../../../components/Icon';
import styles from './styles.module.scss';

interface IProps {
  counters: ProfileRefCounters;
  references: IReferenceScore[];
}

const ProvidedReferencesPopup: FunctionComponent<IProps> = ({ counters, references }) => {
  const countByRefType = (refType: ReferenceType) => filter(({ type }) => type === refType, references).length;

  const isMetRequirements = pipe(
    pickBy((val, key) => (key === 'total'
      ? val > references.length
      : val > countByRefType(key as ReferenceType))),
    isEmpty
  )(counters);

  const Trigger = () => (
    <div>
      <div className={styles.triggerTitle}>
        <span className="t-14 mr-2">
          <FormattedMessage id="contactProfile.candidate.typeProvided" defaultMessage="Reference types provided" />
        </span>
        <Icon className="trigger-arrow" icon="chevronDown" />
      </div>
      <div className={styles.triggerLabel}>
        {isMetRequirements
          ? (
            <>
              <Icon icon="shieldCheck" />
              <FormattedMessage id="contactProfile.candidate.metRequirements" defaultMessage="Met requirements" />
            </>
          )
          : (
            <>
              <Icon className={styles.warningIcon} icon="warning" />
              <FormattedMessage id="contactProfile.candidate.details" defaultMessage="Click for detail" />
            </>
          )}
      </div>
    </div>
  );

  const getBadgeStyle = (val: number, requiredVal: number) => {
    if (val === 0 && requiredVal === 0) return styles.gray;
    if (requiredVal > val) return styles.red;
    return styles.green;
  };

  const Body = () => (
    <div className={styles.popupBody}>
      <header className="avenir-font">
        <FormattedMessage id="contactProfile.candidate.popupTitle" defaultMessage="Provided / Requested" />
      </header>
      <main className={styles.countersContainer}>
        {toPairs(counters).map(([key, requiredVal]) => {
          const isTotal = key === 'total';
          const val = isTotal ? references.length : countByRefType(key as ReferenceType);
          return (
            <div key={key} className="d-flex align-items-center justify-content-between">
              <span><FormattedMessage id={isTotal ? 'total' : key} defaultMessage={isTotal ? 'Total' : key} /></span>
              <div className={`number-badge ${getBadgeStyle(val, requiredVal)}`}>{`${val}/${requiredVal}`}</div>
            </div>
          );
        })}
      </main>
    </div>
  );

  return (
    <div className={styles.referencesPopup}>
      <CustomPopover
        key="reference-counters"
        placement="top"
        rootClose
        trigger={<Trigger />}
        showArrow
        popoverClassName={styles.popover}
      >
        <Body />
      </CustomPopover>
    </div>
  );
};

export default ProvidedReferencesPopup;
